import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Clock, ArrowRight, Lock, Globe, FileText, CheckSquare } from 'lucide-react';
import { motion } from 'framer-motion';

const POSTS = [
  {
    slug: 'how-to-check-website-security',
    icon: <Lock size={20} />,
    color: '#00F0FF',
    category: 'Security Basics',
    title: 'How to Check If Your Website Is Secure',
    description: "A complete step-by-step guide to checking your website's security — from SSL certificates to malware scans. No technical knowledge required.",
    date: 'April 2026',
    readTime: 7,
  },
  {
    slug: 'http-security-headers-explained',
    icon: <Globe size={20} />,
    color: '#7000FF',
    category: 'Technical Guide',
    title: 'What Are HTTP Security Headers and Why They Matter',
    description: "CSP, HSTS, X-Frame-Options — these headers protect your site from XSS, clickjacking, and data theft. Here's what each one does and how to check yours.",
    date: 'April 2026',
    readTime: 8,
  },
  {
    slug: 'ssl-certificate-explained-india',
    icon: <FileText size={20} />,
    color: '#00FF94',
    category: 'Beginner Guide',
    title: 'SSL Certificate Explained for Beginners (India)',
    description: "What is an SSL certificate, why does the padlock matter, and what happens when it expires? Everything Indian website owners need to know.",
    date: 'April 2026',
    readTime: 6,
  },
  {
    slug: 'website-security-checklist-small-business-india',
    icon: <CheckSquare size={20} />,
    color: '#FFD600',
    category: 'Checklist',
    title: 'Website Security Checklist for Small Businesses in India',
    description: 'A practical 10-point security checklist every Indian small business website should follow in 2026. Protect your customers and avoid penalties.',
    date: 'April 2026',
    readTime: 9,
  },
];

const BlogIndexPage = () => {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: '#020210' }}>
      {/* Navbar */}
      <nav style={{
        background: 'rgba(2,2,16,0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(112,0,255,0.3)',
        padding: '0 16px',
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <Shield size={20} color="#00F0FF" />
          <span style={{ fontWeight: 800, fontSize: 17, color: '#fff', fontFamily: 'var(--font-heading)' }}>
            Web<span style={{ color: '#00F0FF' }}>Shield</span>
          </span>
        </div>
        <button
          onClick={() => navigate('/scanner')}
          style={{
            background: 'linear-gradient(135deg,#7000FF,#00F0FF)',
            border: 'none',
            borderRadius: 8,
            padding: '7px 16px',
            color: '#fff',
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Scan My Site →
        </button>
      </nav>

      {/* Hero */}
      <div style={{
        padding: 'clamp(40px,8vw,60px) 16px clamp(32px,6vw,48px)',
        textAlign: 'center',
        background: 'radial-gradient(circle at 50% 0%, rgba(112,0,255,0.1) 0%, transparent 65%)',
        borderBottom: '1px solid rgba(42,42,74,0.5)',
      }}>
        <div style={{
          display: 'inline-block',
          background: 'rgba(0,240,255,0.1)',
          border: '1px solid rgba(0,240,255,0.3)',
          borderRadius: 20,
          padding: '4px 14px',
          fontSize: 11,
          color: '#00F0FF',
          fontWeight: 600,
          marginBottom: 16,
          letterSpacing: 1,
          textTransform: 'uppercase',
        }}>
          Security Knowledge Base
        </div>
        <h1 style={{
          fontSize: 'clamp(24px,7vw,48px)',
          fontWeight: 800,
          color: '#fff',
          marginBottom: 12,
          fontFamily: 'var(--font-heading)',
          lineHeight: 1.2,
        }}>
          Website Security Blog
        </h1>
        <p style={{
          color: '#8898B0',
          fontSize: 'clamp(13px,3.5vw,16px)',
          maxWidth: 520,
          margin: '0 auto',
          lineHeight: 1.6,
        }}>
          Plain-English guides on keeping your website safe. Written for Indian website owners, developers, and small businesses.
        </p>
      </div>

      {/* Articles grid */}
      <div style={{ maxWidth: 1060, margin: '0 auto', padding: 'clamp(32px,6vw,48px) 16px clamp(60px,10vw,80px)' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 440px), 1fr))',
          gap: 'clamp(14px,3vw,24px)',
        }}>
          {POSTS.map((post, i) => (
            <motion.div
              key={post.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              onClick={() => navigate(`/blog/${post.slug}`)}
              style={{
                background: '#0d0d2b',
                border: '1px solid #1a1a3e',
                borderTop: `3px solid ${post.color}`,
                borderRadius: 14,
                padding: 'clamp(20px,4vw,28px)',
                cursor: 'pointer',
                transition: 'transform 0.2s',
              }}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                marginBottom: 14,
                background: `${post.color}18`,
                border: `1px solid ${post.color}33`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: post.color,
                flexShrink: 0,
              }}>
                {post.icon}
              </div>

              <div style={{
                display: 'inline-block',
                fontSize: 10,
                fontWeight: 600,
                color: post.color,
                background: `${post.color}18`,
                borderRadius: 12,
                padding: '2px 10px',
                marginBottom: 10,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}>
                {post.category}
              </div>

              <h2 style={{
                fontSize: 'clamp(15px,3.5vw,18px)',
                fontWeight: 700,
                color: '#fff',
                marginBottom: 8,
                lineHeight: 1.35,
                fontFamily: 'var(--font-heading)',
              }}>
                {post.title}
              </h2>

              <p style={{
                fontSize: 'clamp(12px,2.5vw,14px)',
                color: '#8898B0',
                lineHeight: 1.65,
                marginBottom: 18,
              }}>
                {post.description}
              </p>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: '#4a5a7a' }}>{post.date}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#4a5a7a' }}>
                    <Clock size={10} />{post.readTime} min
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: post.color, fontSize: 13, fontWeight: 600 }}>
                  Read <ArrowRight size={12} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogIndexPage;